import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  getEnumLabel,
  getPriceCategoryTypeOptions,
} from "Forms/Utils/EnumOptions";
import type { PriceCategoryListItemDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { DetailActionCell } from "Shared/DataTable/DetailActionCell";
import { NameLinkCell } from "Shared/NameLinkCell";
import { Price } from "Shared/Price/Price";
import { formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<PriceCategoryListItemDto>();

export const PRICE_CATEGORIES_SORTABLE_COLUMNS = [
  "name",
  "type",
  "dateCreated",
  "dateUpdated",
] as const;

const isSortable = (id: string) =>
  (PRICE_CATEGORIES_SORTABLE_COLUMNS as readonly string[]).includes(id);

export function usePriceCategoriesTableColumns() {
  const { t } = useResource();

  return useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(Resources.PriceCategories.Name),
        enableSorting: isSortable("name"),
        cell: (info) => (
          <NameLinkCell
            path={AppRouting.PriceCategoryDetail.path(
              info.row.original.priceCategoryID,
            )}
          >
            {info.getValue()}
          </NameLinkCell>
        ),
      }),
      columnHelper.accessor("type", {
        header: t(Resources.PriceCategories.Type),
        enableSorting: isSortable("type"),
        cell: (info) =>
          getEnumLabel(getPriceCategoryTypeOptions(), info.getValue()),
      }),
      columnHelper.accessor("commissionRate", {
        header: t(Resources.PriceCategories.CommissionRate),
        enableSorting: isSortable("commissionRate"),
        cell: (info) => `${(info.getValue() * 100).toFixed(0)} %`,
      }),
      columnHelper.accessor("defaultPrice", {
        header: t(Resources.PriceCategories.DefaultPrice),
        enableSorting: isSortable("defaultPrice"),
        cell: (info) => {
          const value = info.getValue();
          return value != null ? <Price amount={value} /> : "-";
        },
      }),
      columnHelper.accessor("dateCreated", {
        header: t(Resources.PriceCategories.DateCreated),
        enableSorting: isSortable("dateCreated"),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.accessor("dateUpdated", {
        header: t(Resources.PriceCategories.DateUpdated),
        enableSorting: isSortable("dateUpdated"),
        cell: (info) => formatDate(info.getValue()),
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 50, justifySelf: "end" },
        cell: (info) => (
          <DetailActionCell
            path={AppRouting.PriceCategoryDetail.path(
              info.row.original.priceCategoryID,
            )}
          />
        ),
      }),
    ],
    [t],
  );
}
